import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float, Stars, Sphere, MeshDistortMaterial } from '@react-three/drei';
import * as THREE from 'three';

function ParticleField() {
  const particlesRef = useRef();
  const count = 1000;

  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 20;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 20;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 20;
    }
    return pos;
  }, []);

  useFrame((state) => {
    if (particlesRef.current) {
      particlesRef.current.rotation.y = state.clock.elapsedTime * 0.08;
      particlesRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.15) * 0.2;
      
      // Pulsing effect
      const positions = particlesRef.current.geometry.attributes.position.array;
      for (let i = 0; i < positions.length; i += 3) {
        const distance = Math.sqrt(
          positions[i] ** 2 + positions[i + 1] ** 2 + positions[i + 2] ** 2
        );
        const wave = Math.sin(distance * 0.5 + state.clock.elapsedTime * 2) * 0.02;
        positions[i + 1] += wave;
      }
      particlesRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.08}
        color="#c41e3a"
        transparent
        opacity={0.9}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

function FloatingRing({ position, rotation, scale }) {
  const ringRef = useRef();

  useFrame((state) => {
    if (ringRef.current) {
      ringRef.current.rotation.z = state.clock.elapsedTime * 0.5;
    }
  });

  return (
    <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
      <mesh ref={ringRef} position={position} rotation={rotation} scale={scale}>
        <torusGeometry args={[1, 0.02, 16, 100]} />
        <meshBasicMaterial color="#c41e3a" transparent opacity={0.6} />
      </mesh>
    </Float>
  );
}

function GlowingSphere({ position }) {
  const sphereRef = useRef();

  useFrame((state) => {
    if (sphereRef.current) {
      sphereRef.current.scale.setScalar(1 + Math.sin(state.clock.elapsedTime * 2) * 0.15);
      sphereRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 1.5) * 0.5;
    }
  });

  return (
    <mesh ref={sphereRef} position={position}>
      <sphereGeometry args={[0.4, 32, 32]} />
      <meshBasicMaterial color="#ff4444" transparent opacity={0.5} />
    </mesh>
  );
}

function DistortedSphere() {
  return (
    <Float speed={3} rotationIntensity={1} floatIntensity={2}>
      <Sphere args={[1, 64, 64]} position={[-4, 0, -6]}>
        <MeshDistortMaterial
          color="#c41e3a"
          attach="material"
          distort={0.4}
          speed={2}
          roughness={0.5}
          transparent
          opacity={0.3}
        />
      </Sphere>
    </Float>
  );
}

function Scene3D() {
  return (
    <>
      <ambientLight intensity={0.3} />
      <pointLight position={[10, 10, 10]} color="#c41e3a" intensity={1.5} />
      <pointLight position={[-10, -10, -10]} color="#8b0000" intensity={0.8} />
      <pointLight position={[0, 5, 5]} color="#ff6b6b" intensity={1} />
      
      <Stars 
        radius={100} 
        depth={50} 
        count={3000} 
        factor={5} 
        saturation={0} 
        fade 
        speed={1.5}
      />
      
      <ParticleField />
      
      <FloatingRing position={[-3, 0, -3]} rotation={[0.5, 0, 0]} scale={1.5} />
      <FloatingRing position={[4, 2, -5]} rotation={[0, 0.5, 0.3]} scale={1} />
      <FloatingRing position={[-2, -2, -4]} rotation={[0.3, 0.2, 0]} scale={0.8} />
      <FloatingRing position={[2, -1, -6]} rotation={[0.8, 0, 0.5]} scale={1.2} />
      
      <GlowingSphere position={[3, 1, -2]} />
      <GlowingSphere position={[-3, -1, -4]} />
      <GlowingSphere position={[0, 2, -5]} />
      
      <DistortedSphere />
      
      <fog attach="fog" args={['#0a0a0a', 5, 20]} />
    </>
  );
}

export default Scene3D;
